import { createContext, useContext, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import API from '@/lib/api';
import { useAuth } from './AuthContext';

const CampusContext = createContext();

export const CampusProvider = ({ children }) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [campuses, setCampuses] = useState([]);
  const [selectedCampus, setSelectedCampusState] = useState(null);
  const [loading, setLoading] = useState(false);

  const isOrgAdmin = user?.role === 'org_admin';
  // Multi-campus admin: role=admin with more than one assigned campus
  const isMultiCampusAdmin = user?.role === 'admin' && (user?.campus_ids?.length > 1);
  const shouldShowSelector = isOrgAdmin || isMultiCampusAdmin;

  // Load campuses when role changes
  useEffect(() => {
    if (shouldShowSelector) {
      loadCampuses();
    } else {
      setCampuses([]);
      setSelectedCampusState(null);
    }
  }, [user?.role, user?.organization_id, JSON.stringify(user?.campus_ids)]);

  // Invalidate queries when campus selection changes
  useEffect(() => {
    if (shouldShowSelector) {
      queryClient.invalidateQueries();
    }
  }, [selectedCampus?.id, shouldShowSelector, queryClient]);

  // Sync URL → state (selectCampus handles state → URL)
  useEffect(() => {
    if (!shouldShowSelector || campuses.length === 0) return;

    const campusId = searchParams.get('campus');
    if (!campusId) return;

    const campus = campuses.find(c => c.id === campusId);
    setSelectedCampusState(prev =>
      prev?.id === (campus?.id ?? null) ? prev : (campus || null)
    );
  }, [searchParams, campuses, shouldShowSelector]);

  const loadCampuses = async () => {
    setLoading(true);
    try {
      const res = await API.get('/campuses');
      // org_admin: returns all org campuses
      // admin: returns only their assigned campuses (backend filters)
      const list = res.data;
      setCampuses(list);

      const campusIdFromUrl = searchParams.get('campus');

      if (campusIdFromUrl) {
        // Restore from URL (page refresh / shared link)
        const fromUrl = list.find(c => c.id === campusIdFromUrl);
        if (fromUrl) {
          setSelectedCampusState(fromUrl);
        } else if (list.length > 0) {
          // URL campus is no longer valid — fall back to first
          selectCampus(list[0]);
        }
      } else if ((isOrgAdmin || isMultiCampusAdmin) && list.length > 0) {
        // Default: auto-select first campus and write to URL
        selectCampus(list[0]);
      }
    } catch (err) {
      console.error('Failed to load campuses:', err);
      setCampuses([]);
    }
    setLoading(false);
  };

  const selectCampus = (campus) => {
    setSelectedCampusState(campus);
    const params = Object.fromEntries(searchParams.entries());
    if (campus) {
      setSearchParams({ ...params, campus: campus.id });
    } else {
      delete params.campus;
      setSearchParams(params);
    }
  };

  // Guard: if selectedCampus was removed from assignments, fall back to first
  useEffect(() => {
    if (!selectedCampus || campuses.length === 0) return;
    if (!campuses.find(c => c.id === selectedCampus.id)) {
      selectCampus(campuses[0] || null);
    }
  }, [campuses, selectedCampus]);

  const value = {
    campuses,
    selectedCampus,
    setSelectedCampus: selectCampus,
    loading,
    isOrgAdmin,
    isMultiCampusAdmin,
    shouldShowSelector,
    reloadCampuses: loadCampuses,
  };

  return (
    <CampusContext.Provider value={value}>
      {children}
    </CampusContext.Provider>
  );
};

export const useCampusContext = () => {
  const context = useContext(CampusContext);
  if (!context) {
    throw new Error('useCampusContext must be used within CampusProvider');
  }
  return context;
};
